// client/src/components/pages/LexiStar/Commissioning/SignOffPanel.tsx (converted from .jsx, ST-00193)
//
// Step 5 of commissioning (LS-09 §5.20; T4-S15): sign the parallel run off
// and issue the Trip Reconciliation Certificate. Blocked below any floor
// unless overridden with a reason AND a second authorised user (SR-21). Since
// ST-00193 the second user is never typed in: the override takes two users,
// each in their own session —
//   1. one asks for it with a reason (Request override → 202, pending);
//   2. a DIFFERENT authorised user opens this step, reads the request and
//      signs THAT request off (the call names it: confirm_override_id).
// If the request changed in between (the requester asked again, or the
// comparison ran again) the server answers 409 override_changed with the
// current one, which the panel then shows for review instead of signing.
// The requester cannot sign their own request; a request expires (default
// 24 h) and can then be asked for again. The database still rejects
// override_second_actor = signed_off_by. The customer keeps the certificate
// either way — an override is printed on it as an exception.

import { useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FaCertificate, FaUserCheck, FaHourglassHalf } from 'react-icons/fa';
import { useAuth } from '../../../../context/AuthContext';
import {
  signOffParallelRun as signOffParallelRunJs,
  pickObject as pickObjectJs,
} from '../../../../services/lexistarAssurance';
import { cardClasses } from '../StatusBoard/atoms';
import { fmtDateTime as fmtDateTimeJs } from '../StatusBoard/format';
import {
  InlineError as InlineErrorJs,
  ReasonField as ReasonFieldJs,
  buttonClasses,
} from '../ReviewQueue/assuranceAtoms';
import { errorMessage as errorMessageJs } from '../ReviewQueue/assuranceShared';
import { floorGate as floorGateJs } from './commissioningLogic';
import { overrideRequestStatus, type PendingOverride } from './overrideRequest';

// ── Shapes (GET /lexistar/parallel-runs/:id, POST …:signOff; m3-contracts §7.1) ──

/** The parallel run as the workspace keeps it (only what this panel reads). */
export interface SignOffRun {
  id: string;
  state?: string | null;
  floors?: Record<string, number> | null;
  certificate_id?: string | null;
  signed_off_by?: string | null;
  signed_off_at?: string | null;
  override_reason?: string | null;
  override_second_actor?: string | null;
  /** The viewer's view of a requested override (ST-00193), or null. */
  pending_override?: PendingOverride | null;
}

interface FloorGate {
  blocked: boolean;
  failing: string[];
  assessed: number;
  noReport: boolean;
}

/** POST …:signOff answers: 200 { certificate, parallel_run } or 202 { pending_override }. */
interface SignOffResponse {
  state?: string;
  pending_override?: PendingOverride | null;
  parallel_run?: SignOffRun;
  certificate?: { id: string; number?: string } | null;
}

export interface SignOffPanelProps {
  run: SignOffRun | null;
  report: unknown;
  onSignedOff?: (run: SignOffRun | null, certificate: SignOffResponse['certificate']) => void;
  /** After an override request is recorded (202): e.g. reload the run. */
  onRequested?: (pending: PendingOverride) => void;
}

const signOffParallelRun = signOffParallelRunJs as unknown as (
  id: string,
  body?: { override_reason?: string; confirm_override_id?: string }
) => Promise<SignOffResponse | null>;

/** The refusals that mean "the request on screen is not the live one any more". */
const STALE_CODES = new Set(['override_changed', 'override_confirmation_required']);

/** A 409's body as the server sends it (code + the current pending_override). */
function staleRequestOf(err: unknown): { pending: PendingOverride | null } | null {
  const data = (err as { response?: { status?: number; data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const d = data as { code?: unknown; pending_override?: unknown };
  if (typeof d.code !== 'string' || !STALE_CODES.has(d.code)) return null;
  const p = d.pending_override;
  return { pending: p && typeof p === 'object' ? (p as PendingOverride) : null };
}
const pickObject = pickObjectJs as unknown as <T>(data: unknown, ...keys: string[]) => T | null;
const fmtDateTime = fmtDateTimeJs as unknown as (iso: string | null | undefined) => string;
const errorMessage = errorMessageJs as unknown as (err: unknown, fallback?: string) => string;
const floorGate = floorGateJs as unknown as (
  report: unknown,
  floors: Record<string, number> | null | undefined
) => FloorGate;
const InlineError = InlineErrorJs as unknown as ComponentType<{ message?: ReactNode }>;
const ReasonField = ReasonFieldJs as unknown as ComponentType<{
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}>;

/** The server's floor on the reason (validation.signOffRunSchema: 10–2000 characters). */
const MIN_REASON = 10;

function PendingBox({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'info' | 'action' | 'muted';
  icon: ReactNode;
  title: ReactNode;
  children: ReactNode;
}) {
  const toneClasses =
    tone === 'action'
      ? 'border-primary-300 bg-primary-50'
      : tone === 'muted'
        ? 'border-slate-200 bg-slate-50'
        : 'border-amber-200 bg-amber-50';
  return (
    <div className={`rounded-lg border p-4 space-y-1.5 ${toneClasses}`}>
      <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <span aria-hidden="true" className="text-slate-700">
          {icon}
        </span>
        {title}
      </p>
      <div className="text-sm text-slate-700 space-y-1">{children}</div>
    </div>
  );
}

export default function SignOffPanel({ run, report, onSignedOff, onRequested }: SignOffPanelProps) {
  const { user } = useAuth();
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A request this panel just recorded (202), until the run is reloaded with it.
  const [requested, setRequested] = useState<PendingOverride | null>(null);
  // The live request as a refused confirm reported it (409 override_changed): shown for review
  // in place of the one the page was loaded with (null: there is none any more).
  const [current, setCurrent] = useState<PendingOverride | null | undefined>(undefined);
  const gate = floorGate(report, run?.floors);
  const signedOff = run?.state === 'signed_off' || !!run?.certificate_id;

  if (!run) return null;

  if (signedOff) {
    return (
      <section className={`${cardClasses} p-5 space-y-2`} aria-label="Signed off">
        <p className="text-base font-bold text-slate-900">Signed off</p>
        <p className="text-sm text-slate-700">
          By {run.signed_off_by || '—'} · {fmtDateTime(run.signed_off_at)}
          {run.override_reason && (
            <>
              {' '}
              — below a floor, overridden; second authorised user {run.override_second_actor}: “
              {run.override_reason}”
            </>
          )}
        </p>
        <Link
          to={`/lexistar/commissioning/${run.id}/certificate`}
          className={buttonClasses.primary}
        >
          <FaCertificate size={12} /> Open the certificate
        </Link>
      </section>
    );
  }

  const pending = current !== undefined ? current : requested || run.pending_override || null;
  const status = gate.blocked && !gate.noReport ? overrideRequestStatus(pending, user) : 'none';

  const post = async (
    body: { override_reason?: string; confirm_override_id?: string },
    fallback: string
  ) => {
    setError(null);
    setBusy(true);
    try {
      const res = await signOffParallelRun(run.id, body);
      if (res?.state === 'awaiting_second_user' && res.pending_override) {
        setRequested(res.pending_override);
        setCurrent(undefined);
        setOverride(false);
        setReason('');
        onRequested?.(res.pending_override);
        return;
      }
      onSignedOff?.(pickObject<SignOffRun>(res, 'parallel_run', 'run'), res?.certificate || null);
    } catch (err) {
      const stale = staleRequestOf(err);
      if (stale) {
        // Not signed: show the request as it is now, to be read before signing it off.
        setCurrent(stale.pending);
        setError(
          stale.pending
            ? 'The override request changed since you opened this page. Read the request below before you sign it off.'
            : 'There is no override request to sign off any more (the comparison was run again). Review the report.'
        );
      } else {
        setError(errorMessage(err, fallback));
      }
    } finally {
      setBusy(false);
    }
  };

  const signOff = () => {
    if (gate.noReport) return setError('Run the comparison and review the report first.');
    return post({}, 'The run was not signed off.');
  };

  // The second user signs off exactly the request on screen (review finding 10, ST-00193).
  const confirmOverride = () => {
    if (!pending?.id) return setError('Reload the page: the override request has no id.');
    return post({ confirm_override_id: pending.id }, 'The run was not signed off.');
  };

  const requestOverride = () => {
    if (!override) return setError('Below a floor: sign-off needs an override.');
    if (reason.trim().length < MIN_REASON)
      return setError(
        `Give the reason for the override (at least ${MIN_REASON} characters) — it is printed on the certificate.`
      );
    return post({ override_reason: reason.trim() }, 'The override was not requested.');
  };

  const requestForm = (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm text-slate-800 min-h-[36px]">
        <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
        Ask for an override (sign off below the floor)
      </label>
      {override && (
        <ReasonField
          value={reason}
          onChange={setReason}
          label="Override reason"
          placeholder="Why the run is accepted below the floor"
        />
      )}
      <p className="text-xs text-slate-500">
        A different authorised user then signs it off from their own login. It expires if nobody
        does within a day.
      </p>
    </div>
  );

  return (
    <section className={`${cardClasses} p-5 space-y-4`} aria-label="Sign-off">
      {gate.noReport ? (
        <p className="text-sm text-slate-700">
          The accuracy report comes first — run the comparison in step 3.
        </p>
      ) : gate.blocked ? (
        <div role="alert" className="space-y-1">
          <p className="text-base font-bold text-slate-900">
            Below a floor: {gate.failing.join(', ')}
          </p>
          <p className="text-sm text-slate-600">
            Fix the misses (step 4 links each to its fix) and run the comparison again, or sign off
            with an override. An override needs a reason and a second authorised user, who signs it
            off in their own session; it is printed on the certificate.
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          <p className="text-base font-bold text-slate-900">Every assessed floor is met</p>
          <p className="text-sm text-slate-600">
            Signing off issues the Trip Reconciliation Certificate with these numbers, the method
            and the floors.
          </p>
        </div>
      )}

      {pending && status === 'mine' && (
        <PendingBox
          tone="info"
          icon={<FaHourglassHalf size={12} />}
          title="Override requested — waiting for a second user"
        >
          <p>
            You asked for it {fmtDateTime(pending.requested_at)}: “{pending.reason}”.
          </p>
          <p>
            A different authorised user must sign it off, from their own login. You cannot sign
            your own request. It expires {fmtDateTime(pending.expires_at)}.
          </p>
        </PendingBox>
      )}

      {pending && status === 'theirs' && (
        <PendingBox
          tone="action"
          icon={<FaUserCheck size={12} />}
          title={`Override requested by ${pending.requested_by}`}
        >
          <p>
            Asked {fmtDateTime(pending.requested_at)}: “{pending.reason}”.
          </p>
          <p>
            Signing off issues the certificate below the floor: {pending.requested_by} is recorded
            as the signer and you as the second authorised user. It expires{' '}
            {fmtDateTime(pending.expires_at)}.
          </p>
        </PendingBox>
      )}

      {pending && status === 'expired' && (
        <PendingBox tone="muted" icon={<FaHourglassHalf size={12} />} title="Override request expired">
          <p>
            {pending.requested_by} asked for an override {fmtDateTime(pending.requested_at)}, but no
            second user signed it off before {fmtDateTime(pending.expires_at)}. Ask for it again.
          </p>
        </PendingBox>
      )}

      {gate.blocked && !gate.noReport && (status === 'none' || status === 'expired') && requestForm}

      <InlineError message={error} />
      <div className="flex justify-end">
        {gate.noReport || !gate.blocked ? (
          <button
            type="button"
            onClick={signOff}
            disabled={busy || gate.noReport}
            className={buttonClasses.primary}
          >
            <FaCertificate size={12} />{' '}
            {busy ? 'Signing off…' : 'Sign off and issue the certificate'}
          </button>
        ) : status === 'theirs' ? (
          <button
            type="button"
            onClick={confirmOverride}
            disabled={busy}
            className={buttonClasses.primary}
          >
            <FaUserCheck size={12} /> {busy ? 'Signing off…' : 'Sign off this request as second user'}
          </button>
        ) : status === 'mine' ? null : (
          <button
            type="button"
            onClick={requestOverride}
            disabled={busy || !override}
            className={buttonClasses.primary}
          >
            <FaCertificate size={12} /> {busy ? 'Requesting…' : 'Request override'}
          </button>
        )}
      </div>
    </section>
  );
}
